import clsx from 'clsx';
import { Heading, Icon } from 'copilot-design-system';
import { ReactNode, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Channel as ChannelType } from 'stream-chat';
import {
  Channel,
  ChannelProps,
  SendButtonProps,
  SuggestionList,
  SuggestionListProps,
  UnreadMessagesSeparatorProps,
  useAttachmentManagerState,
  useMessageComposer,
} from 'stream-chat-react';

import { ChatContentLoader } from 'src/components/chat/inner-channel/ChatContentLoader';
import { FileUploadButton } from 'src/components/chat/inner-channel/custom-message-input/ActionButtons';
import { CustomMessageInput } from 'src/components/chat/inner-channel/custom-message-input/CustomMessageInput';
import { CustomAutocompleteSuggestionItem } from 'src/components/chat/inner-channel/CustomAutoCompleteSuggestionItem';
import {
  CustomDateSeparator,
  NewDateSeparator,
} from 'src/components/chat/inner-channel/CustomDateSeparator';
import { CustomSendButton } from 'src/components/chat/inner-channel/CustomSendButton';
import { SendButton } from 'src/components/chat/inner-channel/SendButton';
import { FlagsContext } from 'src/context';

interface ChannelProviderProps {
  MessageNotification: ChannelProps['MessageNotification'];
  EmptyStateIndicator?: ChannelProps['EmptyStateIndicator'];
  channel?: ChannelType;
  markReadOnMount?: boolean;
  showMessagingSidebar?: boolean;
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
  } | null;
  children: ReactNode;
}

const CurrentFileUploadButton = () => {
  const { isUploadEnabled } = useAttachmentManagerState();
  const messageComposer = useMessageComposer();

  if (!isUploadEnabled) {
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      messageComposer.attachmentManager.uploadFiles(files);
      e.target.value = '';
    }
  };

  return (
    <div className="str-chat__file-input-container">
      <label className="str-chat__file-input-label">
        <input
          id="file-upload-input"
          className="str-chat__file-input"
          type="file"
          onChange={handleChange}
          multiple
        />
        <Icon icon="Upload" className="w-6 h-6" />
      </label>
    </div>
  );
};

const CustomSuggestionList = (props: SuggestionListProps) => {
  const { EnableChatUIUpdates } = useContext(FlagsContext);
  return (
    <SuggestionList
      {...props}
      containerClassName={clsx(
        props.containerClassName,
        'z-10',
        EnableChatUIUpdates && '!py-1',
      )}
    />
  );
};

const CustomUnreadMessagesSeparator = ({
  unreadCount,
}: UnreadMessagesSeparatorProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-fit py-0.5 bg-info-background">
      <Heading className="text-[#053299]" size="xs">
        {unreadCount
          ? t(($) => $.messages.unread_messages, { count: unreadCount })
          : t(($) => $.messages.unread_messages_default)}
      </Heading>
    </div>
  );
};

export const ChannelProvider = ({
  MessageNotification,
  EmptyStateIndicator,
  channel,
  markReadOnMount,
  showMessagingSidebar = false,
  aiAssistantStatus = null,
  children,
}: ChannelProviderProps) => {
  const { EnableChatUIUpdates } = useContext(FlagsContext);
  const currentSendButton = useCallback(
    (props: SendButtonProps) => (
      <SendButton {...props} showMessagingSidebar={showMessagingSidebar} />
    ),
    [showMessagingSidebar],
  );
  const customSendButton = useCallback(
    (props: SendButtonProps) => (
      <CustomSendButton {...props} aiAssistantStatus={aiAssistantStatus} />
    ),
    [aiAssistantStatus],
  );
  const disableComponentCallback = useCallback(() => null, []);

  return (
    <Channel
      channel={channel}
      markReadOnMount={markReadOnMount}
      SendButton={EnableChatUIUpdates ? customSendButton : currentSendButton}
      AttachmentSelector={
        EnableChatUIUpdates ? undefined : CurrentFileUploadButton
      }
      AttachmentSelectorInitiationButtonContents={
        EnableChatUIUpdates ? FileUploadButton : undefined
      }
      DateSeparator={
        EnableChatUIUpdates ? NewDateSeparator : CustomDateSeparator
      }
      LoadingIndicator={ChatContentLoader}
      EmptyStateIndicator={EmptyStateIndicator}
      TypingIndicator={disableComponentCallback}
      activeUnreadHandler={disableComponentCallback} // `activeUnreadHandler` updates the document.title with unread count, but we don't want this because we have our own unread count in the header
      UnreadMessagesSeparator={
        EnableChatUIUpdates
          ? CustomUnreadMessagesSeparator
          : disableComponentCallback
      }
      UnreadMessagesNotification={disableComponentCallback} // Hides the unread message fab
      MessageNotification={
        EnableChatUIUpdates ? MessageNotification : undefined
      }
      Input={EnableChatUIUpdates ? CustomMessageInput : undefined}
      AutocompleteSuggestionList={CustomSuggestionList}
      AutocompleteSuggestionItem={
        EnableChatUIUpdates ? CustomAutocompleteSuggestionItem : undefined
      }
    >
      {children}
    </Channel>
  );
};
